import React, { Fragment } from 'react';
import { withCookies } from 'react-cookie';
import { withRouter } from 'react-router-dom';
    
import { IconButton, Tooltip, Grid, Typography, Paper, Button, Collapse, TextField } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import GetAppIcon from '@material-ui/icons/GetApp';
import SettingsIcon from '@material-ui/icons/Settings';

import { Booking } from '../models/Booking';
import { CalEvent } from '../models/CalEvent';
import { ThemeColors } from '../Theme'
import { Global, Constructors } from '../models/Global'
import { PagedSlotList } from '../models/PagedSlotList'

import EventSignupPage from './EventSignupPage'
import { DateTime } from 'luxon';

import { RefundPopover } from '../components/RefundPopover'
import { EditPatronPopover } from '../components/EditPatronPopover'
import { deleteSlotConfirmation } from '../components/DeleteSlotConfirmation';
import { NotifyPatronPopover } from '../components/NotifyPatronPopover';
import { TooltipForSlot } from '../components/CommonCalendarFunctions';
import { PatronQuestionsViewer } from '../components/PatronQuestionsViewer';

import { RestComponent, DateUtils, ManageBitwiseCheckboxes, DateRangeSelector, PopupMenu, TextEntryPopover } from 'react-frontend-utils'

import { DateConversions } from '../utils/DateConversions'
import { MultiDayEvent } from '../models/MultiDayEvent';



/**
 * 
 * Required props:
 * type - "Booking" or "Event" or "Availability" or "Block"
 * viewSlotInCalendarCallback - function to call to view a slot in the calendar, takes the Slot obj, and a boolean doEdit to indicate if the slot should be opened for editing
 * eventid - an Event id to manage signups for
 */

export class SlotsListPage extends RestComponent {
  
    styles = {
        paperLabel: {
            marginLeft: 15,
            marginRight: 15,
            marginBottom: 5,
            color: 'gray',
            fontSize: '9pt',
            flexGrow: 1
        },
        paper: {
            padding: 0,
            marginBottom: 20
        },
        id: {
            marginLeft: 'auto',
            maxWidth: 300,
        },
        table: {
            borderCollapse: 'collapse',
            width: '100%',
            marginBottom: 10,
            marginTop: 10
        },
        tableHeader: {
            borderBottom: '2px solid ' + ThemeColors.appBarBackground,
            textAlign: 'left',
            paddingBottom: 5,
            paddingRight: 10,
            marginBottom: 8,
            fontSize: 13,
            color: ThemeColors.darkGray,
            fontWeight: 'normal',
            textTransform: 'uppercase'
        },
        tableDataWithBorder: {
            textAlign: 'left',
            fontSize: 13,
            paddingRight: 10,
            borderBottom: '1px solid lightGray'
        },
        tableData: {
            textAlign: 'left',
            fontSize: 14,
            marginRight: 10,
            verticalAlign: 'center'
        },
        status: {
            margin: 'auto',
            alignContent: 'center',
            width: 230,
            textTransform: 'uppercase',
            padding: 2, 
            borderRadius: 2, 
            color: 'white', 
            textAlign: 'center'
        },
        details: {
            marginLeft: 30,
            marginBottom: 5,
            padding: 5,
            textAlign: 'left',
            borderRadius: 3,
            backgroundColor: ThemeColors.veryLightGray,
        },
        journal: {
            marginLeft: 30,
            marginBottom: 5,
        }
       
    };
    
    
    _currentDatabase = Global.getLastDatabase();

    // Now based on the browser time and we'll work with UTC in the calendar component, converting to the timezone on fetch
    _now = DateTime.now(); 

    _startOfThisMonth = this._now.startOf('month');
    _endOfThisMonth = this._startOfThisMonth.plus({month: 1}).minus({days: 1});

    _startOfLastMonth = this._startOfThisMonth.minus({month: 1});
    _startOfYear = this._now.startOf('year');
    _endOfYear = this._startOfYear.plus({year: 1}).minus({days: 1});
    _endOfNextYear = this._now.plus({year: 1}).endOf('year');

     //options for the search time range pulldown
    _searchTimeRangeOptions = [
        {label: "This month", startTime: this._startOfThisMonth.toUTC().valueOf(), endTime: this._endOfThisMonth.toUTC().valueOf()},
        {label: "Last month", startTime: this._startOfThisMonth.minus({month: 1}).toUTC().valueOf(), endTime: this._startOfThisMonth.minus({days: 1}).toUTC().valueOf()},
        {label: "Last 90 days", startTime: this._now.minus({days: 90}).toUTC().valueOf()},
        {label: "This year", startTime: this._startOfYear.toUTC().valueOf(), endTime: this._endOfYear.toUTC().valueOf()},
        {label: "Upcoming", startTime: this._now.startOf('day').toUTC().valueOf(), endTime: this._endOfNextYear.toUTC().valueOf()},
    ];
    
    _checkboxesRef = React.createRef(); 

    _resources = [];

    constructor(props) {
        super(props);
    
        this.state.slotsPage = null;         //last fetched page - a PagedSlotList
               
        // Defaults, in case the user selects nothing
        this.state.searchFromDate = DateConversions.luxonDateTimeToJsonDateString(this._startOfYear).split("T")[0];     // keep date only
        this.state.searchToDate = DateConversions.luxonDateTimeToJsonDateString(this._endOfYear).split("T")[0];         // keep date only
        
        this.state.stateCheckboxes = 0;           //Bitwise selected types to search for, from checkboxes only! (bit 0 = pending, bit 1 = active, bit 2 = cancelled)
        this.state.revealSet = new Set();         //Set of slots to reveal detail
        this.state.patronDataToSearch = "";       //Data in a patron name or email to search for
        this.state.textDataToSearch = "";       //Data in a slot title or description to search for

        this.state.refundPopupOpen = false;
        this.state.maxRefund = 0;
        this.state.slotCurrency = null;
        this.state.refundSlot = null
        this.state.commentPopupOpen = false;
        this.state.commentSlot = null;
        this.state.manageBookingPatronSlot = null;      // For Bookings, the Booking (slot) to manage patrons for
        this.state.editBookingPatronPopoverOpen = false;

        this.state.notifyPatronPopoverOpen = false;
        this.state.slotToPromptForMessage = null;
        this.state.isDeletingSlot = false;  
        this.state.notifyPatronEmailFunction = null;
        this.state.suppressPatronEmailFunction = null;  

        this.state.patronWithQuestions = null;   // Patron object with questions to view
        this.state.patronQuestionsPopoverOpen = false;

        this.state.eventid = null;   // event to manage signups for
        if (props.eventid)
            setTimeout(() => this._manageEventSignup({id: props.eventid}));

    }
    
    componentDidUpdate() {  // when the a non-Event SlotListPage is requested, close the EventSignupPage
        if (this.props.type !== "Event" && this.state.eventid)
            this.setState({eventid: null});
    }
    
    
    /**
     * When the page loads, immediately fetch the first page of Slots
     */
    componentDidMount() {
        super.componentDidMount();
        window.addEventListener("databaseChangeEvent", this._databaseChanged);
        
        //fetch all resources, then slots, first page
        this._fetchResources();  
    }
    
  
    componentWillUnmount() {
        super.componentWillUnmount();
        window.removeEventListener("databaseChangeEvent", this._databaseChanged);
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.type !== nextProps.type) {

            if (this._checkboxesRef.current)
                this._checkboxesRef.current.reset();  //reset the checkboxes
            this.setState({stateCheckboxes: 0, revealSet: new Set(), patronDataToSearch: "", textDataToSearch: ""});
            setTimeout(() => this._fetchResources());  //refetch when type changes
        }
    }


    // When the database changes, refetch the resources (which will refresh the calendar) and fetch the timezone
    _databaseChanged = () => {
        if (Global.getLastDatabase() !== this._currentDatabase) {
            console.log("Database changed to " + Global.getLastDatabase() + ", refetching slots");
            this._currentDatabase = Global.getLastDatabase();
            this.setState({slotsPage: null, eventid: null});         // clear list of slots
            this._fetchResources();         // refetch
        }
    }
    

    _fetchResources = () => {
        this.incrementBusy();
        this.secureJSONFetch("/bk/" + Global.getLastDatabase() + "/resources", {}, this._fetchResourcesCallback, this._fetchErrorCallback); 
    }
    
    // When the resources are fetched, update the list of resources. If this is the first time, check all the resources,
    // otherwise, only check the resources that were previously checked and are still in the list.
    // Then, refresh the calendar
    _fetchResourcesCallback = (response) => {
        this.decrementBusy();
        if (response) {
            this._resources = response.map((json) => Constructors.resourcefromJson(json, Global.getTimezone()));
        }
        this._fetchSlots();
    }
    
    
    //Fetch a specific page, using search fields if set.
    //If forDownload is set, download as csv, otherwise normal fetch to display
    _fetchSlots = (page = 0, forDownload = false) => {
        
        // Convert to luxon in our timezones
        let start, end;
        if (this.state.searchFromDate)
            start = DateConversions.jsonDateStringToLuxonDateTime(this.state.searchFromDate + "T00:00:00", Global.getTimezone());
        if (this.state.searchToDate)
            end = DateConversions.jsonDateStringToLuxonDateTime(this.state.searchToDate + "T23:59:59", Global.getTimezone());

        // Then back to JSON for the fetch
        if (start)
            start = DateConversions.luxonDateTimeToJsonDateString(start); 
        if (end)
            end = DateConversions.luxonDateTimeToJsonDateString(end); 
       
        this.setState({slotsPage: null});  //clear the list of slots

        let queryString = "?page=" + page;
                        
        //state checkboxes are set, and we are searching Bookings or Events
        if (this.state.stateCheckboxes && (this.props.type === "Booking" || this.props.type === "Event"))
            queryString += "&states=" + this.state.stateCheckboxes;

        if (start)
            queryString += "&fromDate=" + start;
        if (end)
            queryString += "&toDate=" + end;
        
       
        if (this.state.textDataToSearch)
            queryString += "&text=" + encodeURIComponent(this.state.textDataToSearch);

        // Patron data to search is set, and we are searching Bookings or Events
        if (this.state.patronDataToSearch && (this.props.type === "Booking" || this.props.type === "Event"))
            queryString += "&patronData=" + encodeURIComponent(this.state.patronDataToSearch);
        
        if (forDownload) {
            const filename = this.props.type + " Export on " + DateUtils.downloadTimeString() + ".csv";
            this.secureFileDownload("/bk/" + this._currentDatabase + "/slots/" + this.props.type + "/download" + queryString, filename, null, this._fetchErrorCallback); 
        }
        else {
            this.incrementBusy();
            this.secureJSONFetch("/bk/" + this._currentDatabase + "/slots/" + this.props.type, {}, this._fetchSlotsCallback, this._fetchErrorCallback, queryString); 
        }
                
    }
    
    //Callback for fetching Slots - response is json of PagedSlotList
    _fetchSlotsCallback = (response) => {
        if (response) {            
            const sl = new PagedSlotList(response);
            this.setState({slotsPage: sl});
        }            
        this.decrementBusy();
    }
    

    _fetchErrorCallback = (error) => {
        this.showConfirmAlert("Error", error, 'red');
        this.decrementBusy();
    }

    _slotModifiedCallback = (response) => {
        this.decrementBusy();
        this.setState({notifyPatronPopoverOpen: false});
        this._refresh();
    }

  
    //If the slot is in the reveal list, remove it, if it is not, add it (toggle the transaction view)
    _revealSlotDetail = (s) => {
        const toReveal = this.state.revealSet;
        
        if (toReveal.has(s))
            toReveal.delete(s);
        else
            toReveal.add(s);
        
        this.setState({revealSet: toReveal});
    }
    

    //fetch the previous page
    _prevPage = () => {  
        
        if (!this.state.slotsPage || this.state.slotsPage.isFirst())
            return;
        
        this._fetchSlots(this.state.slotsPage.pageNo-1); 
        window.scrollTo(0, 0);  //jump to the top

    }
    
    //fetch the next page
    _nextPage = () => {  
        
        if (!this.state.slotsPage || this.state.slotsPage.isLast())
            return;
        
        this._fetchSlots(this.state.slotsPage.pageNo+1); 
        window.scrollTo(0, 0);  //jump to the top
    }
    
    
    _dateChangeCallback = (start, end) => {
        this.setState({searchFromDate: start, searchToDate: end});
    }
    
    _dateParseError = (label, error) => {
        this.showConfirmAlert("Error in Date Field \"" + label + "\"", error, 'red');
    }
    

    _checkboxStateChanged = (json, newValue) => {
        this.setState({stateCheckboxes: newValue});
    }
    
    _download = () => {
        this._fetchSlots(0, true);  //fetch for download        
    }

    _refresh = () => {
        this._fetchSlots(this.state.slotsPage ? this.state.slotsPage.pageNo : 0);   //just fetch the same page again
    }
    
    _search = () => {
        this._fetchSlots(0, false);
    }


    _addComment = (slot) => {
        this.setState({commentPopupOpen: true, commentSlot: slot});
    }

    _approveBooking = (slot) => {

        if (!slot.approved && (slot.state === "CANCELLED" || slot.state === "PENDING_CANCEL")) {
            this.showConfirmAlert("Confirm", "Are you sure you want to approve a cancelled booking?", 'black', 
            "Cancel",  () => this._doApproveBooking(slot), "Yes, Approve", 'red');
        }
        else
            this._doApproveBooking(slot);
    }

    _doApproveBooking = (slot) => {

        const completeApprove = (notifyPatron, msg) => {
            
            console.log("Approve/Unapprove slot, notifyPatrons: " + notifyPatron + " custom msg: " + (msg ? "yes" : "no"));

            const body = {notify: notifyPatron};
            if (notifyPatron)
                body.customMsg = msg;

            this.incrementBusy();
            const unapprove = slot.approved ? "?unapprove=true" : "";
            this.secureJSONFetch("/bk/slots/" + slot.id + "/approve" + unapprove, {method: 'POST', body: JSON.stringify(body)}, this._slotModifiedCallback, this._fetchErrorCallback);
        }

        this.setState({notifyPatronPopoverOpen: true, slotToPromptForMessage: slot, isDeletingSlot: false,
                       notifyPatronEmailFunction: (msg) => completeApprove(true, msg),
                       suppressPatronEmailFunction: () => completeApprove(false)});   
    }
        
    _commentOkCallback = (text) => {
        this.setState({commentPopupOpen: false});
        if (!text || text.trim().length === 0) {
            this.showConfirmAlert("Error", "No comment was entered", 'red');
            return;
        }

        this.incrementBusy();
        this.secureJSONFetch("/bk/slots/" + this.state.commentSlot.id + "/comment", {method: 'POST', body: text}, this._slotModifiedCallback, this._fetchErrorCallback);
    }

    
    _managePatrons = (slot) => {
        this.setState({manageBookingPatronSlot: slot, editBookingPatronPopoverOpen: true});
    }

    _finishEditBookingPatrons = (contactName, email, phone, enableSms, patrons) => {
        const booking = this.state.manageBookingPatronSlot;
        booking.patron.contactName = contactName;
        booking.patron.patronEmail = email;
        booking.patron.patronPhone = phone;
        booking.patron.enableSms = enableSms;
        booking.patron.patronNames = patrons;
        booking.patron.patronCount = patrons.length;
        this.setState({editBookingPatronPopoverOpen: false});
        this.incrementBusy();

        const body = {slot: booking.toJsonForPost(false), notify: false, customMsg: null};

        this.secureJSONFetch("/bk/slots", {method: 'POST', body: JSON.stringify(body)}, this._slotModifiedCallback, this._fetchErrorCallback);
    }


    _viewPatronQuestions = (slot) => {
        this.incrementBusy();
        // questions are normally redacted so we need to fetch the slot directly which will include them
        this.secureJSONFetch("/bk/slots/" + slot.id, {}, this._fetchSlotForPatronQuestionsCallback, this._fetchErrorCallback); 
    }

    _fetchSlotForPatronQuestionsCallback = (response) => {
        this.decrementBusy();
        if (response) {
            const slot = Constructors.slotFromJson(response, Global.getTimezone());
            if (slot instanceof Booking && slot.patron && slot.patron.hasPatronQuestions())
                this.setState({patronWithQuestions: slot.patron, patronQuestionsPopoverOpen: true});
            else {
                this.showConfirmAlert("No Patron Questions", "There are no patron questions for this Booking.", 'black');
            }
        }
    }

    // user selects "Delete", show a confirm alert
    _deleteSlot = (slot) => {
        deleteSlotConfirmation(slot, true, this.showConfirmAlert, this._confirmDeleteSlot);
    }

    // callback from the confirm alert when the user confirms the delete, try and delete it from the server
    _confirmDeleteSlot = (slot) => {

        const doDelete = (notifyPatron, msg) => {
            
            console.log("Deleting slot, notifyPatrons: " + notifyPatron + " custom msg: " + (msg ? "yes" : "no"));
            this.incrementBusy();

            const body = {notify: notifyPatron};
            if (notifyPatron)
                body.customMsg = msg;

            // have to use PATCH here because DELETE with a body is not supported by all browsers/servers
            this.secureJSONFetch("/bk/slots/" + slot.id, {method: 'PATCH', body: JSON.stringify(body)}, this._slotModifiedCallback, this._fetchErrorCallback);
        }

        if (slot.hasPatrons()) {
            this.setState({notifyPatronPopoverOpen: true, slotToPromptForMessage: slot, isDeletingSlot: true,
                           notifyPatronEmailFunction: (msg) => doDelete(true, msg),
                           suppressPatronEmailFunction: () => doDelete(false)});
        }
        else 
            doDelete(false);
    }

    _refundBooking = (booking) => {   
        this.setState({refundPopupOpen: true, maxRefund: booking.patron.calculatedCost - booking.patron.refundedAmount, slotCurrency: booking.patron.isoCurrency, refundSlot: booking});
    }

    _startRefund = (amount, comment) => {
        this.setState({refundPopupOpen: false});
        this.incrementBusy();

        const slot = this.state.refundSlot;
        const body = {amount: amount, comment: comment};
        this.secureJSONFetch("/bk/slots/" + slot.id + "/refund", {method: 'POST', body: JSON.stringify(body)}, 
                             this._slotModifiedCallback, this._fetchErrorCallback);
    }

            
    _manageEventSignup = (event) => {   
        this.props.history.push("/bkportal?eventid=" + event.id);
        this.setState({eventid: event.id});
    }

    _markCashPayment = (booking, paid) => {
        this.incrementBusy();
        this.secureJSONFetch("/bk/slots/" + booking.id + "/bookingMarkCashPayment?paid=" + paid, {method: 'POST'}, 
                             this._slotModifiedCallback, this._fetchErrorCallback);
    }


    //Action items are items for the right popup menu
    _getActionItems = (slot) => {
        
        const canModifySlot = slot.canEdit(this._resources);

        const actionItems = [];

        if (slot.resourceIds && slot.resourceIds.length > 0)    // with no Resources, it won't be visible in the Calendars
            actionItems.push({label: "Edit on Calendar Page", selectCallback: () => {this.props.viewSlotInCalendarCallback(slot, true)}, icon: null});

        if (slot.resourceIds && slot.resourceIds.length > 0)    // with no Resources, it won't be visible in the Calendars
            actionItems.push({label: "Show in Calendar", selectCallback: () => {this.props.viewSlotInCalendarCallback(slot, false)}, icon: null});

        if (slot instanceof CalEvent) {
            if (slot instanceof MultiDayEvent && !slot.isMaster())
                actionItems.push({label: "Check-In Patrons", selectCallback: () => {this._manageEventSignup(slot);}, icon: null});
            else
                actionItems.push({label: "Edit Signup Settings / Patrons", selectCallback: () => {this._manageEventSignup(slot);}, icon: null});
        }

        if (slot instanceof Booking)
            actionItems.push({label: "View Patron Questions", selectCallback: () => {this._viewPatronQuestions(slot);}, icon: null});

        if ((slot instanceof Booking || slot instanceof CalEvent) && canModifySlot)
            actionItems.push({label: "Comment", selectCallback: () => {this._addComment(slot);}, icon: null});

        if (slot instanceof Booking && canModifySlot)
            actionItems.push({label: slot.approved ? "Unapprove" : "Approve", selectCallback: () => {this._approveBooking(slot);}, icon: null});

        const inBookingProcess = slot instanceof Booking && slot.inBookingByPatron();

        if (slot.state === "CREATED" && !inBookingProcess && canModifySlot)
            actionItems.push({label: "Delete", selectCallback: () => {this._deleteSlot(slot);}, icon: null});

        if (slot.state === "ACTIVE" && !inBookingProcess && canModifySlot)
            actionItems.push({label: "Delete/Cancel", selectCallback: () => {this._deleteSlot(slot);}, icon: null});

        if (slot instanceof Booking && slot.refundable() && canModifySlot)
            actionItems.push({label: "Refund", selectCallback: () => {this._refundBooking(slot);}, icon: null});

        if (slot instanceof Booking && (slot.state === "ACTIVE" && !slot.inBookingByPatron()) && canModifySlot)
            actionItems.push({label: "Edit Patrons", selectCallback: () => {this._managePatrons(slot);}, icon: null});

        if (slot instanceof Booking && slot.patron.isCashPayment && canModifySlot) {
            if (!slot.patron.cashPaymentPaid)
                actionItems.push({label: "Mark as Paid", selectCallback: () => {this._markCashPayment(slot, true);}, icon: null});
            else
                actionItems.push({label: "Mark as Unpaid", selectCallback: () => {this._markCashPayment(slot, false);}, icon: null});
        }

        return actionItems;
    }

    //Render a row of the table with the specified Slot. In the Slot is null, render the header row
    _renderRow = (slot, key) => {
        

        const hasService = (this.props.type === "Booking" || this.props.type === "Availability") && window.innerWidth > 1400;   // only show Service column for Bookings and Availabilities, and only on wide screens
        const isEventOrBooking = this.props.type === "Booking" || this.props.type === "Event";
        const isBooking = this.props.type === "Booking";
        const isBlock = this.props.type === "Block";

        let columns = 7;
        if (hasService)
            columns++;
        if (isBooking)
            columns++;
        if (isBlock)
            columns++;

        //Render the header
        if (!slot) {
            return (
                <tr key={key} style={this.styles.tableStyle}>
                    <th style={{...this.styles.tableHeader, paddingRight: 0, width: '30px'}}/>
                    <th style={{...this.styles.tableHeader, width: 250}}>Start Date</th>
                    <th style={{...this.styles.tableHeader, width: 130}}>Duration (min)</th>
                    {hasService ? 
                        <th style={{...this.styles.tableHeader, width: 200}}>Service</th>
                        : null
                    }
                    {isBlock ? 
                        <th style={{...this.styles.tableHeader, width: 200}}>Blocked Services/Resources</th>
                        : null
                    }
                    {isEventOrBooking ?
                        <Fragment>
                            <th style={{...this.styles.tableHeader, width: 160, textAlign: 'center'}}>{this.props.type === "Booking" ? "Payments" : "Signup Count"}</th>
                            <th style={{...this.styles.tableHeader, textAlign: 'center'}}>Status</th>
                            {isBooking ? <th style={{...this.styles.tableHeader, width: 100, textAlign: 'center'}}>Approved</th> : null}
                        </Fragment>
                        : null
                    }
                    <th style={{...this.styles.tableHeader, width: 250}}>{this.props.type + " Name"}</th>
                    <th style={{...this.styles.tableHeader, paddingRight: 0, width: '60px'}}/>
                </tr>
            );
        }
            
        const isRevealed = this.state.revealSet.has(slot);
        const leftIcon = isRevealed ? <ArrowDropDownIcon/> : <ArrowRightIcon/>;
        const startDate = DateConversions.dateTimeStr(slot.start);
        const duration = slot.duration() / 60;
    
        let col3 = null;
        if (slot instanceof Booking)
            col3 = <td style={this.styles.tableData}>{slot.state === "CREATED" ? "" : slot.patron.paymentBadge()}</td>;
        else if (slot instanceof CalEvent)
            col3 = <td style={{...this.styles.tableData, textAlign: 'center'}}>{slot.state === "CREATED" ? "" : slot.totalPatronCount()}</td>;

        let title = <span>{slot.title}
                        {slot.isRecurring() ? <span style={{marginLeft: 15}}>↻</span> : null}
                        {slot instanceof MultiDayEvent && slot.isMaster() ? <span style={{marginLeft: 15}}>Ⓜ️</span> : null}
                    </span>;

        let serviceTitle = "?";
        if (slot && hasService) { 
            const service = slot.getService(this._resources);
            if (service)
                serviceTitle = service.name;
        }

        let blockedResources = "";
        if (isBlock)
            blockedResources = slot.getResources(this._resources).map((r) => r.name).join(", ");
                                
        return (
            <Fragment key={key}>
                <tr style={this.styles.tableStyle}>

                    <td style={this.styles.tableData}>
                        {isEventOrBooking ?
                            <IconButton edge="start" onClick={() => this._revealSlotDetail(slot)}>
                                {leftIcon}
                            </IconButton>
                            : null
                        }
                    </td>

                    <td style={this.styles.tableData}>{startDate}</td>
                    <td style={this.styles.tableData}>{duration}</td>

                    {hasService ? 
                        <td style={this.styles.tableData}>{serviceTitle}</td>
                        : null
                    }
                    {isBlock ?
                        <td style={this.styles.tableData}>{blockedResources}</td>
                        : null
                    }
                    
                    {isEventOrBooking ?
                        <Fragment>
                            {col3}
                            <td style={this.styles.tableData}>
                                <div style={{...this.styles.status, backgroundColor: slot.colorForStatus()}}>
                                    {slot.status()}
                                </div>
                            </td>
                            {isBooking ? 
                                <td style={{...this.styles.tableData, width: 100, textAlign: 'center'}}>{slot.approved ? "✅" : ""}</td> 
                                : null
                            }
                        </Fragment>
                        : null
                    }
                   
                    <td style={this.styles.tableData}>{title}</td>

                    <td style={{...this.styles.tableData, paddingRight: 0}}>
                        <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 20}}>
                            <TooltipForSlot slot={slot} resources={this._resources}> 
                                <Typography variant='body2' style={{color: 'blue', fontWeight: 'bold', fontSize: 16, cursor: 'default'}}>
                                    ⓘ
                                </Typography>
                            </TooltipForSlot> 
                            <PopupMenu menuIcon={(<SettingsIcon style={{fontSize: '20', color: ThemeColors.settingsOlive}}/>)}  
                                        menuItems={this._getActionItems(slot)} 
                                        menuTooltipText={"Actions"}/>
                        </div>
                    </td>
                </tr>
                <tr style={this.styles.tableStyle}>
                    <td colSpan={columns} align='center' style={this.styles.tableDataWithBorder}>
                        {isEventOrBooking ?

                            <Collapse in={isRevealed}>
                                <Typography variant='body2' style={{marginLeft: 35, fontWeight: 'bold'}}>Details</Typography>
                                <div style={this.styles.details} dangerouslySetInnerHTML={{ __html: slot.details() }} />
                                <div style={this.styles.journal}>
                                    {slot.renderJournal()}
                                </div>
                            </Collapse>
                            : null
                        }
                    </td>
                </tr>
            </Fragment>
        );
    }
    
    //Callback when the EventPage is finished
    _manageSignupFinished = () => {
        this.setState({eventid: null});
        this._refresh();
    }
    
    //------------------------------- RENDER ----------------------------------------------
    
    render() {
       
        //If we have an Event to manage signups for, show the EventPage
       if (this.state.eventid) 
            return <EventSignupPage id={this.state.eventid} exitCallback={this._manageSignupFinished} viewSlotInCalendarCallback={this.props.viewSlotInCalendarCallback}/>;
                                
        const page = this.state.slotsPage;

        let checkboxLabels = [];
        if (this.props.type === "Booking") {
            checkboxLabels.push({name: "Reserved", tooltip: "Patron has reserved the Booking"});
            checkboxLabels.push({name: "Booked", tooltip: "The Booking is confirmed (payment may be pending)"});
            checkboxLabels.push({name: "Pending Cancel", tooltip: "A cancel request is pending for the Booking"});
            checkboxLabels.push({name: "Cancelled", tooltip: "The Booking has been cancelled"});
        }
        else if (this.props.type === "Event") {
            checkboxLabels.push({name: "Created", tooltip: "The Event has been created"});
            checkboxLabels.push({name: "Active", tooltip: "At least one Patron has signed up for the Event"});
            checkboxLabels.push({name: "Pending Cancel", tooltip: "A cancel request is pending for the Event"});
            checkboxLabels.push({name: "Cancelled", tooltip: "The Event has been cancelled"});
        }
       
        const showing = (page && page.slots.length > 0) ? "Displaying " + (page.index + 1) + "-" + (page.index + page.slots.length) : null;            
                
        return (                        
             <Fragment>
                {this.getConfirmAlertComponent()}   

                {this.state.manageBookingPatronSlot && this.state.editBookingPatronPopoverOpen ?
                    <EditPatronPopover isOpen={this.state.editBookingPatronPopoverOpen} patron={this.state.manageBookingPatronSlot.patron}
                                              okCallback={this._finishEditBookingPatrons} cancelCallback={() => this.setState({editBookingPatronPopoverOpen: false})}/>
                    : null
                }

                <TextEntryPopover isOpen={this.state.commentPopupOpen} showSkip={false} multiline={true} title={"Add Comment for " + this.props.type} 
                                  okCallback={this._commentOkCallback} cancelCallback={() => this.setState({commentPopupOpen: false})}/>

                <RefundPopover isOpen={this.state.refundPopupOpen} maxVal={this.state.maxRefund} currency={this.state.slotCurrency}
                               okCallback={this._startRefund} cancelCallback={() => this.setState({refundPopupOpen: false})}/>                 
           
                <NotifyPatronPopover isOpen={this.state.notifyPatronPopoverOpen} slot={this.state.slotToPromptForMessage} isDeletingSlot={this.state.isDeletingSlot}
                                     isForApproval={!this.state.isDeletingSlot}
                                     cancelCallback={() => this.setState({notifyPatronPopoverOpen: false})} 
                                     sendEmailCallback={this.state.notifyPatronEmailFunction} suppressEmailCallback={this.state.suppressPatronEmailFunction}/>

                <PatronQuestionsViewer isOpen={this.state.patronQuestionsPopoverOpen} patron={this.state.patronWithQuestions}
                                       closeCallback={() => this.setState({patronQuestionsPopoverOpen: false})}/>


                <div style={{display: 'flex', gap: 20, marginLeft: 10, marginRight: 10, justifyContent: 'right', alignItems: 'center'}}>
            
                    {this.state.isBusy ? this.getBusyComponent('left', {marginLeft: 20, padding: 15}, 30) : 
                        <Tooltip title="Refresh">
                            <div style={{display: 'flex'}}>
                                <IconButton edge="end" onClick={this._refresh} color='primary' style={{marginLeft: 0, marginRight: 1, marginBottom: 1}}>
                                    <RefreshIcon fontSize="large"/>
                                </IconButton>
                            </div>
                        </Tooltip>}
           
                </div>
                
                <Paper style={this.styles.paper}>
                    
                    <Typography variant="body2" style={this.styles.paperLabel}>{"Search " + this.props.type + "s"}</Typography>  

                    <Grid container direction="row" spacing={3} style={{padding: 20}}>

                        <Grid item md={4} sm={6} xs={12}>

                            <DateRangeSelector calendarColor={ThemeColors.calendarColor}
                                                dateFormat={null}
                                                timeOptions={this._searchTimeRangeOptions}
                                                allowFuture={true}
                                                minYear={2024}
                                                maxYear={DateUtils.currentYear() + 50}
                                                ref={this._dateRangeRef}
                                                initialTimeRange="This year"
                                                initialStartDate={this.state.searchFromDate}
                                                initialEndDate={this.state.searchToDate}
                                                onDateChange={this._dateChangeCallback}
                                                onParseError={this._dateParseError}/>

                        </Grid>

                        {checkboxLabels.length > 0 ?
                            <Grid item md={4} sm={12} xs={12}>
                                <ManageBitwiseCheckboxes
                                    key={JSON.stringify(checkboxLabels)}
                                    ref={this._checkboxesRef}
                                    style={{marginTop: -17}}
                                    json="stateCheckboxes"
                                    label="States"
                                    labels={checkboxLabels}
                                    onChange={this._checkboxStateChanged}
                                    initialValue={this.state.stateCheckboxes}
                                />
                            </Grid>
                            : null
                        }   

                        <Grid item md={4} sm={12} xs={12}>
                            <Tooltip title={"Search text fields: " + this.props.type + " title/description (may be slow)"}>
                                <TextField label="Title/Description Search" inputProps={{style: {fontSize: 14}}} value={this.state.textDataToSearch} style={{marginBottom: 20}}
                                           onChange={(event) => this.setState({textDataToSearch: event.target.value.trim()})} variant="outlined" fullWidth={true} InputLabelProps={{ shrink: true}} />
                            </Tooltip>
                            {this.props.type === "Booking" || this.props.type === "Event" ?
                                <Tooltip title="Search patron names (may be slow)">
                                    <TextField label="Patron Email/Name Search" inputProps={{style: {fontSize: 14}}} value={this.state.patronDataToSearch}
                                            onChange={(event) => this.setState({patronDataToSearch: event.target.value.trim()})} variant="outlined" fullWidth={true} InputLabelProps={{ shrink: true}} />
                                </Tooltip>
                                : null
                            }
                        </Grid>
                    </Grid>

                    <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>

                        <Tooltip title={"Search and Display " + this.props.type + "s below"}>
                            <Button fullWidth onClick={this._search} variant="outlined" color='primary' style={{margin: 20, maxWidth: 200}} component="label" startIcon={<SearchIcon />}>
                                Search
                            </Button>
                        </Tooltip>

                        <Tooltip title={"Search and Download " + this.props.type + "s"}>
                            <Button fullWidth onClick={this._download} variant="outlined" color='primary' style={{margin: 20, maxWidth: 200}} component="label" startIcon={<GetAppIcon/>}>
                                Download
                            </Button>
                        </Tooltip>

                    </div>

                </Paper>
                
                <div style={{marginTop: 15}}/>
                
                {page ?
                     <div>   
                     
                        <table style={this.styles.table}>
                            <thead>
                               {this._renderRow(null, 0) /*render header*/ }
                            </thead>
                            <tbody>
                                {page.slots.map((slot, index) => this._renderRow(slot, index+1))}
                            </tbody>
                        </table>
                        <div style={{width: '100%', display: 'flex', alignItems: 'center'}}>
                            <Typography variant="body2" style={{color: ThemeColors.darkGray}}>{showing}</Typography> 
                            <div style={{marginLeft: 'auto'}}>
                                {!page.isFirst() ? <Button onClick={this._prevPage} variant="outlined" color='primary' component="label" >
                                               Prev
                                           </Button>
                                           : null}

                                {!page.isLast() ? <Button onClick={this._nextPage} variant="outlined" color='primary' style={{marginLeft: 10}} component="label" >
                                               Next
                                           </Button>
                                           : null}
                            </div>            
                        </div>                        

                    </div>
                    
                    
                : null}
                
                
            </Fragment>
        );
        
    }
}



export default withCookies(withRouter(SlotsListPage));

